import { useState, useEffect } from 'react'
import axios from 'axios'

export const useMarketData = () => {
  const [marketData, setMarketData] = useState({
    totalMarketCap: null,
    totalMarketCapChange: null,
    ethGasPrice: null,
    ethGasPriceUSD: null,
    tradingVolume: null,
    tradingVolumeChange: null,
    btcDominance: null,
    ethDominance: null,
    otherDominance: null,
    fearGreedValue: null,
    fearGreedLabel: null,
    loading: true
  })
  
  const [gasData, setGasData] = useState([])
  const [volumeData, setVolumeData] = useState([])

  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        const apiKey = process.env.REACT_APP_COINRANKING_API_KEY || 'coinranking388b85577906af3b50b3cfee19c2406f12850f83e7c5f843'
        
        const [gasResponse, coinRankingResponse, ethKlinesResponse, btcKlinesResponse, fearGreedResponse] = await Promise.all([
          axios.get('https://ethgasstation.info/api/ethgasAPI.json').catch(() => null),
          axios.get('https://api.coinranking.com/v2/coins?limit=100', {
            headers: {
              'x-access-token': apiKey
            }
          }).catch(() => null),
          axios.get('https://api.binance.com/api/v3/klines', {
            params: {
              symbol: 'ETHUSDT',
              interval: '5m',
              limit: 14
            }
          }).catch(() => null),
          axios.get('https://api.binance.com/api/v3/klines', {
            params: {
              symbol: 'BTCUSDT',
              interval: '5m',
              limit: 14
            }
          }).catch(() => null),
          axios.get('https://api.alternative.me/fng/?limit=1').catch(() => null)
        ])

        if (gasResponse?.data) {
          const gasPrice = parseFloat(gasResponse.data.safeLow) / 10
          const gasPriceUSD = (gasPrice * 0.000000021).toFixed(3)
          setMarketData(prev => ({
            ...prev,
            ethGasPrice: gasPrice.toFixed(9),
            ethGasPriceUSD: gasPriceUSD
          }))
        }

        if (coinRankingResponse?.data?.data?.coins) {
          const coins = coinRankingResponse.data.data.coins
          const totalVolume = coins.reduce(
            (sum, coin) => sum + parseFloat(coin['24hVolume'] || 0),
            0
          )
          const totalMarketCapRaw = coins.reduce(
            (sum, coin) => sum + parseFloat(coin.marketCap || 0),
            0
          )
          const btcMarketCapRaw = coins
            .filter((coin) => coin.symbol === 'BTC')
            .reduce((sum, coin) => sum + parseFloat(coin.marketCap || 0), 0)
          const ethMarketCapRaw = coins
            .filter((coin) => coin.symbol === 'ETH')
            .reduce((sum, coin) => sum + parseFloat(coin.marketCap || 0), 0)

          const btcDominance = totalMarketCapRaw ? (btcMarketCapRaw / totalMarketCapRaw) * 100 : 0
          const ethDominance = totalMarketCapRaw ? (ethMarketCapRaw / totalMarketCapRaw) * 100 : 0
          const otherDominance = Math.max(0, 100 - btcDominance - ethDominance)

          const marketCapT = (totalMarketCapRaw / 1000000000000).toFixed(2)
          const volumeB = (totalVolume / 1000000000).toFixed(2)
          
          setMarketData(prev => {
            let volumeChange = null
            let marketCapChange = null
            const prevVolume = localStorage.getItem('prevTradingVolume')
            const prevMarketCap = localStorage.getItem('prevTotalMarketCap')
            
            if (prevVolume) {
              const change = ((parseFloat(volumeB) - parseFloat(prevVolume)) / parseFloat(prevVolume)) * 100
              volumeChange = change.toFixed(2)
            }

            if (prevMarketCap) {
              const change = ((parseFloat(marketCapT) - parseFloat(prevMarketCap)) / parseFloat(prevMarketCap)) * 100
              marketCapChange = change.toFixed(2)
            }
            
            localStorage.setItem('prevTradingVolume', volumeB)
            localStorage.setItem('prevTotalMarketCap', marketCapT)
            
            return {
              ...prev,
              totalMarketCap: marketCapT,
              totalMarketCapChange: marketCapChange,
              tradingVolume: volumeB,
              tradingVolumeChange: volumeChange,
              btcDominance: btcDominance.toFixed(1),
              ethDominance: ethDominance.toFixed(1),
              otherDominance: otherDominance.toFixed(1),
              loading: false
            }
          })
        } else {
          setMarketData(prev => ({ ...prev, loading: false }))
        }

        if (ethKlinesResponse?.data) {
          const ethPrices = ethKlinesResponse.data.map(kline => parseFloat(kline[4]))
          setGasData(ethPrices)
        }

        if (btcKlinesResponse?.data) {
          const btcPrices = btcKlinesResponse.data.map(kline => parseFloat(kline[4]))
          setVolumeData(btcPrices)
        }

        if (fearGreedResponse?.data?.data?.[0]) {
          const fearGreed = fearGreedResponse.data.data[0]
          setMarketData(prev => ({
            ...prev,
            fearGreedValue: parseFloat(fearGreed.value),
            fearGreedLabel: fearGreed.value_classification,
          }))
        }
      } catch (error) {
        console.error('Error fetching market data:', error)
        setMarketData(prev => ({ ...prev, loading: false }))
      }
    }

    fetchMarketData()
    
    const interval = setInterval(() => {
      fetchMarketData()
    }, 100000)
    
    return () => clearInterval(interval)
  }, [])

  return { marketData, gasData, volumeData }
}

